import { createPortal } from 'react-dom';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { TONE_CLASSES } from '../../foundation/toneClasses.js';

const VARIANT_ICONS = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
  neutral: Info,
};

const toVariantConfig = (variant) => {
  const tone = TONE_CLASSES[variant] ?? TONE_CLASSES.info;
  return {
    containerClass: tone.container,
    iconClass: tone.icon,
    titleClass: tone.title,
    messageClass: tone.message,
    Icon: VARIANT_ICONS[variant] ?? Info,
  };
};

const ToastItem = ({ item, onClose, defaultMaxWidth, defaultMinWidth }) => {
  const config = toVariantConfig(item.variant);
  const StatusIcon = config.Icon;
  const maxW = item.maxWidth ?? defaultMaxWidth;
  const minW = item.minWidth ?? defaultMinWidth;

  return (
    <div
      className={[
        'pointer-events-auto flex w-full max-w-[var(--toast-max-width)] min-w-[var(--toast-min-width)]',
        'items-start gap-3 rounded-lg border p-4 shadow-lg',
        'animate-[toast-in_0.25s_ease-out]',
        config.containerClass,
      ].join(' ')}
      style={{
        '--toast-max-width': maxW,
        '--toast-min-width': minW,
      }}
      role="alert"
    >
      <StatusIcon size={20} className={['mt-0.5 shrink-0', config.iconClass].join(' ')} />
      <div className="min-w-0 flex-1">
        {item.title && (
          <p className={['text-sm font-semibold', config.titleClass].join(' ')}>{item.title}</p>
        )}
        {item.message && (
          <p className={['text-sm', item.title ? 'mt-0.5' : '', config.messageClass].join(' ')}>
            {item.message}
          </p>
        )}
      </div>
      {item.action && (
        <button
          type="button"
          onClick={() => item.onAction?.()}
          className={[
            'mt-0.5 shrink-0 rounded text-sm font-semibold',
            item.actionVariant === 'primary'
              ? 'bg-[#2173FF] px-3 py-1 text-white hover:bg-[#002F80] dark:bg-blue-600 dark:hover:bg-blue-800'
              : ['px-2 py-1 hover:bg-black/10 dark:hover:bg-white/10', config.titleClass].join(' '),
          ].join(' ')}
        >
          {item.action}
        </button>
      )}
      {item.canClose && (
        <button
          type="button"
          onClick={() => onClose(item.id)}
          className="mt-0.5 shrink-0 rounded p-0.5 text-gray-400 hover:text-content-hover"
          aria-label="Close"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

const ToastViewport = ({ items, position, onClose, defaultMaxWidth, defaultMinWidth }) => {
  if (items.length === 0) return null;

  return createPortal(
    <div
      className={['fixed z-[9999] flex flex-col gap-2 pointer-events-none', position].join(' ')}
    >
      {items.map((item) => (
        <ToastItem
          key={item.id}
          item={item}
          onClose={onClose}
          defaultMaxWidth={defaultMaxWidth}
          defaultMinWidth={defaultMinWidth}
        />
      ))}
    </div>,
    document.body,
  );
};

export default ToastViewport;
